import { useCallback, useState } from 'react';

// ----------------------------------------------------------------------
// Tour progress is stored per browser for now.
//
// This hook is the single seam between the tour feature and wherever progress
// is kept. When the `preferences` column lands on the `profile` table, only the
// read/write bodies below change -- no consumer of this hook needs touching.
// ----------------------------------------------------------------------

const STORAGE_KEY = 'em.tours.v1';
// An interactive guide spans several pages, so its position has to survive a
// full page load -- not just a React re-render. Kept separate from the "seen"
// map above because it is in-flight state, not history.
const ACTIVE_KEY = 'em.tours.active.v1';

export function readActiveGuide() {
  try {
    const raw = window.localStorage.getItem(ACTIVE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

export function writeActiveGuide(value) {
  try {
    if (value) window.localStorage.setItem(ACTIVE_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(ACTIVE_KEY);
  } catch (error) {
    // Guide still runs, it just won't resume after a reload.
  }
}

function readAll() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    // Private browsing, blocked site data, or malformed JSON. Treat as "nothing
    // seen yet" rather than breaking the page the tour is meant to help with.
    return {};
  }
}

function writeAll(next) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    // Nothing to do -- the tour still runs, it just won't be remembered.
  }
}

export default function useTourPersistence() {
  const [progress, setProgress] = useState(readAll);

  const update = useCallback((tourId, patch) => {
    setProgress((prev) => {
      const next = { ...prev, [tourId]: { ...prev[tourId], ...patch } };
      writeAll(next);
      return next;
    });
  }, []);

  // A tour counts as seen only at the version it was seen at, so bumping a
  // tour's `version` re-offers it to people who already watched the old one.
  const hasSeen = useCallback(
    (tour) => progress[tour.id]?.seenVersion === tour.version,
    [progress]
  );

  // Starting a tour only records progress -- not that it has been seen. A tour
  // the user abandons half way should be offered again next time.
  const markStarted = useCallback((tour) => update(tour.id, { lastStep: 0 }), [update]);

  // Dismissing counts as seen: the user made a choice, so don't nag them again.
  const markDismissed = useCallback(
    (tour) => update(tour.id, { seenVersion: tour.version, completed: false }),
    [update]
  );

  const markStep = useCallback(
    (tour, stepIndex) => update(tour.id, { lastStep: stepIndex }),
    [update]
  );

  const markCompleted = useCallback(
    (tour) => update(tour.id, { seenVersion: tour.version, completed: true, lastStep: 0 }),
    [update]
  );

  const resetAll = useCallback(() => {
    setProgress({});
    writeAll({});
    writeActiveGuide(null);
  }, []);

  return { progress, hasSeen, markStarted, markStep, markDismissed, markCompleted, resetAll };
}
